import * as React from "react"
import { useEffect, useState } from "react"
import { graphql, navigate, useStaticQuery } from "gatsby"
import * as Yup from "yup"

import getCookie from "../../lib/getCookie"
import { MyForm, MyTextInput, MyNumberInput, MySubmitButton, MyTextarea, MyCheckbox, MyCheckboxGroup, MyUploadButton, MyDatepicker, MySelect } from "./formElements"
import cloudinaryUpload from "../../lib/cloudinaryUpload"

const HubSpotForm = ({ bgColor, formTitle, formName, formSubtitle, pageName, location }) => {
  const data = useStaticQuery(graphql`
    query HubSpotFormQuery {
      allHubspotForm {
        nodes {
          name
          guid
          thankYouMessageJson
          redirect
          portalId
          submitText
          formFieldGroups {
            fields {
              label
              name
              required
              hidden
              fieldType
              options {
                label
                value
              }
            }
          }
        }
      }
    }
  `)
  
  const [formData, setFormData] = useState("")
  const [validationSchema, setValidationSchema] = useState({})
  const fileRef = React.createRef(null)
  const [secureUrl, setSecureUrl] = React.useState("")
  const [fileName, setFileName] = React.useState("No file selected")

  const defaultForm = data.allHubspotForm.nodes.find(
    node => node.name === "Contact us form"
  )
  const formToUse = data.allHubspotForm.nodes.find(
    node => node.name === formName
  ) || defaultForm

  const { guid, submitText, formFieldGroups } = formToUse

  const flattenedFields = formFieldGroups.map(group => group.fields).flat()

  useEffect(() => {
    initForm(flattenedFields)
  }, [])

  const initForm = (formSchema) => {
    let _formData = {}
    let _validationSchema = {}

    formSchema.forEach(element => {
      _formData[element.name] = ""

      if (element.fieldType === "text") {
        _validationSchema[element.name] = Yup.string()
      } else if (element.fieldType === "textarea") {
        _validationSchema[element.name] = Yup.string()
      } else if (element.fieldType === "checkbox") {
        _validationSchema[element.name] = Yup.array()
      } else if (element.fieldType === "number") {
        _validationSchema[element.name] = Yup.number()
      }

      if (element.required === true) {
        _validationSchema[element.name] = _validationSchema[element.name].required('Required')
      }
    })

    setFormData(_formData)
    setValidationSchema(Yup.object().shape({ ..._validationSchema }))
  }

  const getFormElement = (element) => {
    const props = {
      name: element.name,
      label: element.label
    }

    if(element.options.length > 0) {
      props.options = element.options
    }

    if (element.name === "secure_url") {
      return <MyUploadButton onChange={fileUpload} ref={fileRef} fileName={fileName} {...props}/>
    } else if (element.hidden) {
      return
    } else if (element.fieldType === "text") {
      return <MyTextInput {...props} />
    } else if (element.fieldType === "textarea") {
      return <MyTextarea {...props} />
    } else if (element.fieldType === "number") {
      return <MyNumberInput {...props} />
    } else if (element.fieldType === "checkbox") {
      return (
        <MyCheckboxGroup key={element.name} legend={element.label}>
          {element.options.map(option => (
            <MyCheckbox
              key={option.label}
              name={element.name}
              label={option.label}
              value={option.value}
            />
          ))}
        </MyCheckboxGroup>
      )
    } else if (element.fieldType === "date") {
      return <MyDatepicker {...props} />
    } else if (element.fieldType === "select") {
      return <MySelect {...props} />
    }
  }

  const fileUpload = (event) => {
    event.preventDefault()

    cloudinaryUpload(fileRef.current.files[0], fileRef.current.files[0].fileName, "bgea8oy9")
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        return response.json()
      })
      .then((data) => {
        setSecureUrl(data.secure_url)
        setFileName(data.original_filename + "." + data.format)
      })
      .catch(error => {
        alert("There has been an issue with your file upload, please try again or contact us directly.", error)
      })
  }

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    let body = {}
    let fields = []
    const hutk = getCookie("hubspotutk")

    for (const [name, value] of Object.entries(values)) {
      if (Array.isArray(value)) {
        let products = value.join(";")
        fields.push({
          name: name,
          value: products,
        })
      } else if (name === "file") {
      } else {
        fields.push({
          name: name,
          value: value,
        })
      }
    }

    fields.push({
      name: "secure_url",
      value: secureUrl,
    })

    body.fields = fields
    body.context = {
      hutk: hutk,
      pageUri: `www.etchingexpressions.com${location.pathname}`,
      pageName: pageName,
    }

    const url =`/.netlify/functions/hubspot-submit?formGuid=${guid}`
    const options = {
      method: "POST",
      body: JSON.stringify(body),
    }

    fetch(url, options)
      .then(response => response.json())
      .then(data => {
        if (data.inlineMessage) {
          alert(data.inlineMessage)
          resetForm()
        } else if (data.redirectUri) {
          navigate("/pages/thank-you-for-your-question/")
        } else if (data.errors) {
          throw data
        }
      })
      .catch(error => {
        error.errors.forEach(el => console.log(el.errorType, el.message))
        alert(error.message)
      })

    setSubmitting(false)
  }

  return (
    <MyForm
      enableReinitialize
      initialValues={formData}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      bgColor={bgColor}
      formTitle={formTitle}
      formSubtitle={formSubtitle}
    >
      {formTitle && (
        <div className="column is-full has-text-centered">
          <h3 className="title is-4 is-uppercase">
            {formTitle}
          </h3>
          {formSubtitle && (
            <div className="subtitle is-6">
              {formSubtitle}
            </div>
          )}
        </div>
      )}

      {formFieldGroups.map((fieldGroup) => {
        return (
          fieldGroup.fields.map(field => {
            return (
              <div key={field.name} className={fieldGroup.fields.length > 1 ? "column is-half" : "column is-full"}>
                {getFormElement(field)}
              </div>
            )
          })
        )
      })}

      <div className="column is-full">
        <MySubmitButton title={submitText} />
      </div>
    </MyForm>
  )
}

export default HubSpotForm
